/**
 * AI 메일 검토 패널 — ComposeModal 옆에 붙어 AI 제안을 GitHub 스타일로 보여줌.
 *
 * 흐름:
 *  1) 패널 마운트 → 백엔드 SSE `POST /api/mail/ai/review` 호출
 *  2) chunk 이벤트가 올 때마다 화면에 즉시 추가 (스트리밍)
 *  3) done 이벤트 또는 EOF 시 diff 계산
 *  4) "적용" 버튼 → 부모 ComposeModal 의 본문을 AI 버전으로 교체
 *
 * 디자인 (단일 컬럼):
 *  ┌─────────────────────────────────────────────┐
 *  │ AI 검토 (gpt-4.1)              [복사][↻][✕] │
 *  ├─────────────────────────────────────────────┤
 *  │ 안내문                                        │
 *  ├─────────────────────────────────────────────┤
 *  │ AI 제안                                       │
 *  │ ─ (수정·추가된 내용을 색으로 표시) ─           │
 *  │ ─ 빨강:   원본에서 빠진 줄 (삭제)              │
 *  │ ─ 파랑:   원본과 다르게 바뀐 줄 (수정)         │
 *  │ ─ 초록:   완전히 새로 추가된 줄                │
 *  │ ─ 흰색:   원본과 동일한 줄                     │
 *  ├─────────────────────────────────────────────┤
 *  │ 프롬프트 (편집 가능 — 기본값 미리 채워짐)        │
 *  │ [textarea ...]                                │
 *  ├─────────────────────────────────────────────┤
 *  │ [닫기]  [새 답변 받기]            [적용]       │
 *  └─────────────────────────────────────────────┘
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import CloseRoundedIcon       from "@mui/icons-material/CloseRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import RefreshRoundedIcon     from "@mui/icons-material/RefreshRounded";
import CheckRoundedIcon       from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { diffWordsWithSpace } from "diff";

import { ds }     from "../../../../styles/designSystem";
import { notify } from "../../../../util/toast";
import { copyToClipboard } from "../../../../util/clipboard";
import ActionButton from "../../../../component/common/ActionButton";
import { mailApi } from "../../api/mailApi";
import { extractBlocks, toNumbered, parseNumberedWithFallback, applyRevisions } from "./aiBodyRevise";
import LoadingDots from "../../../../component/common/LoadingDots";

/** 백엔드 adminPrompt 과 동일하게 유지. 사용자가 패널에서 편집하면 이 값을 시작점으로. */
/** 백엔드 미응답 시 fallback. 평소엔 GET /api/mail/ai/prompt-setting 응답을 사용. */
const FALLBACK_INSTRUCTION = `당신은 한국 사내 메일·공문 첨삭 전문가입니다.
입력은 [N] 형식으로 번호 매겨진 단락 목록입니다. 각 [N] 은 메일 본문의 한 단락,
표의 한 셀, 또는 리스트의 한 항목 텍스트입니다.
출력은 같은 번호로 시작하는 줄을 같은 개수로 반환합니다.

★ 가장 중요한 원칙 ★
"원문을 그대로 반복하지 않는다." 모든 단락을 적극적으로 다시 표현합니다.
의미·일정·숫자만 보존하고 문장 구조·어휘·연결어를 재구성해 더 정중하고 명확하게 만듭니다.
"괜찮은 문장이니 그대로 두자" 식의 보수적 판단은 금지합니다.

규칙:
1) [N] 번호와 줄 개수는 1:1 그대로 유지 (없는 번호 만들거나, 합치거나 빼지 않습니다).
2) 공공기관·외부 거래처·임원급에게 보낼 수 있는 격식 있는 경어체로 다시 씁니다.
   "~합니다" 보다는 "~드립니다 / ~말씀드립니다 / ~드리오니" 등 정중한 형태를 적극 활용합니다.
3) 첫 번째 [N] 단락에는 반드시 정중한 인사말을 자연스럽게 포함합니다
   (예: "안녕하십니까. 늘 변함없는 관심과 도움에 감사드립니다." 등).
   이미 인사말이 있어도 더 정중하게 보강합니다.
4) 마지막 [N] 단락에는 반드시 정중한 마무리 인사를 포함합니다
   (예: "감사합니다.", "검토 후 회신 주시면 감사하겠습니다." 등).
5) 의미·요청사항·일정·숫자·고유명사는 절대 임의로 추가/삭제/변경하지 않습니다.
6) 그 외 모든 표현은 자유롭게 재구성합니다:
   - 한 문장이 길면 두 문장으로 나눕니다.
   - 명사구·수동태로 늘어진 문장은 능동·정중형으로 바꿉니다.
   - 같은 단어 반복은 다른 표현으로 변경합니다.
   - 평이한 표현은 격식 있는 표현으로 (예: "보냅니다" → "송부드립니다").
7) 표 셀·리스트 항목은 의미 유지하며 호칭·존칭·맞춤법만 정돈.
8) 구어체·이모티콘·마크다운은 제거. 평문만.
9) 출력은 [N] 형식 줄들만. 다른 설명·머리말·코드블록 없이.
10) 첫 번째 [N] 단락의 인사말은 반드시 풍성하게 작성합니다.
    계절·날씨·안부를 한두 문장 분량으로 길게 펼쳐 본문에 들어가기 전 워밍업 역할을 하도록 합니다.
    예시:
    - "안녕하십니까. 무더운 여름 날씨에 늘 건강 잘 챙기고 계신지요. 항상 아낌없는 관심과 지원에 깊이 감사드립니다."
    - "안녕하십니까. 환절기 일교차가 큰 시기에 건강 유의하시기 바라며, 늘 변함없는 협력에 감사드립니다."
    - "안녕하십니까. 연말 마무리로 분주하실 텐데도 늘 변함없이 챙겨 주시는 마음에 깊이 감사드립니다."
    계절·시기에 어울리는 표현을 적극 활용해 한두 문장 더 추가합니다.`;

/* ──────────────────────────────────────────────────────────────────
 *  SSE 호출
 *  fetch + ReadableStream 직접 파싱. EventSource 는 GET 만 가능하므로 POST 본문을
 *  넘기려면 이 방식이 필요. 인증 헤더와 baseURL 은 axios 정책과 동일.
 *  ────────────────────────────────────────────────────────────── */
async function streamReview({ subject, bodyText, instruction, onChunk, onDone, onError, onModel, signal }) {
    try {
        const baseUrl = process.env.REACT_APP_API_REAL_URL || "";
        const token   = localStorage.getItem("auth_token");
        const headers = {
            "Content-Type": "application/json",
            "Accept": "text/event-stream",
        };
        if (token) headers["X-TOKEN"] = token;

        const res = await fetch(`${baseUrl}/api/mail/ai/review`, {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify({
                subject:     subject || "",
                bodyText:    bodyText || "",
                instruction: instruction || "",
            }),
            signal,
        });
        if (!res.ok) {
            const errText = await res.text().catch(() => "");
            throw new Error(`AI 검토 호출 실패 (${res.status}) ${errText}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            let idx;
            while ((idx = buffer.indexOf("\n\n")) >= 0) {
                const raw = buffer.slice(0, idx);
                buffer = buffer.slice(idx + 2);
                let dataLines = [];
                let currentEvent = "chunk";
                for (const ln of raw.split("\n")) {
                    if (ln.startsWith("event:")) currentEvent = ln.slice(6).trim();
                    else if (ln.startsWith("data:")) dataLines.push(ln.slice(5));
                }
                const data = dataLines.join("\n");
                if (currentEvent === "done") { onDone?.(); return; }
                if (currentEvent === "error") { onError?.(new Error(data)); return; }
                if (currentEvent === "model" && data) {
                    let modelName = data;
                    try { modelName = JSON.parse(data); } catch { /* fallback */ }
                    if (modelName) onModel?.(modelName);
                }
                if (currentEvent === "chunk" && data) {
                    let payload = data;
                    try { payload = JSON.parse(data); } catch { /* 호환 fallback */ }
                    if (payload) onChunk?.(payload);
                }
            }
        }
        onDone?.();
    } catch (e) {
        if (e.name === "AbortError") return;
        onError?.(e);
    }
}

/* ──────────────────────────────────────────────────────────────────
 *  인라인 word-level diff
 *
 *  한 단락 안에서 원본 vs AI 수정본을 비교해 단어 단위로 추가/삭제/유지를
 *  같은 줄에 inline span 으로 렌더한다. 별도 줄로 빨간/파란 줄 분리 X.
 *
 *  렌더 형태 예:
 *    "안녕 [-하세요-][+하십니까+], 김선우 입니다."
 *    삭제 단어: 빨강 + 취소선
 *    추가 단어: 초록 + 굵게
 *    유지 단어: 일반 텍스트
 *  ────────────────────────────────────────────────────────────── */
function InlineDiff({ original, revised }) {
    const parts = diffWordsWithSpace(original || "", revised || "");
    return (
        <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {parts.map((p, i) => {
                if (p.removed) {
                    return (
                        <Box key={i} component="span" sx={{
                            bgcolor: "#ffebe9", color: "#82071e",
                            textDecoration: "line-through",
                            px: 0.3, borderRadius: 0.4,
                        }}>{p.value}</Box>
                    );
                }
                if (p.added) {
                    return (
                        <Box key={i} component="span" sx={{
                            bgcolor: "#dbeafe", color: "#1e3a8a",
                            fontWeight: 700,
                            px: 0.3, borderRadius: 0.4,
                        }}>{p.value}</Box>
                    );
                }
                return <Box key={i} component="span">{p.value}</Box>;
            })}
        </Box>
    );
}

export default function AiReviewPanel({ originalHtml = "", subject = "", onClose, onApply, onApplySubject }) {
    const [streaming, setStreaming]     = useState(false);
    const [suggestion, setSuggestion]   = useState("");
    const [error, setError]             = useState(null);
    const [instruction, setInstruction]   = useState(FALLBACK_INSTRUCTION);
    /** 관리자가 정한 사내 표준 prompt — 마운트 시 fetch. "기본값으로 되돌리기" 시 이 값으로 복귀. */
    const [adminPrompt, setAdminPrompt]   = useState(FALLBACK_INSTRUCTION);
    const [showDiff, setShowDiff]         = useState(false); // 변경사항 보기 토글 (기본 OFF — 평문만)
    const [modelName, setModelName]     = useState("");  // 백엔드가 알려주는 실제 사용 모델

    /** 본문 검토와 동시에 받는 제목 추천 — 패널 상단에 칩으로 표시. */
    const [subjectSuggestions, setSubjectSuggestions] = useState([]);
    const [subjectLoading, setSubjectLoading]         = useState(false);

    const abortRef = useRef(null);

    /**
     * originalHtml 에서 텍스트가 있는 안전한 블록(p / li / td 셀 등)만 추출.
     * 이미지·표 자체는 입력에 포함하지 않으므로 AI 가 건드릴 수 없다.
     * doc 은 동일 객체를 적용 시 재사용해 element 를 직접 수정 → innerHTML 직렬화.
     */
    const { doc, items } = useMemo(() => extractBlocks(originalHtml), [originalHtml]);
    const numberedOriginal = useMemo(() => toNumbered(items), [items]);
    const hasRevisable = items.length > 0;

    const start = () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        if (!hasRevisable) {
            setError("검토할 텍스트가 없습니다. (이미지·표만 있는 본문은 검토하지 않습니다)");
            setStreaming(false);
            setSuggestion("");
            return;
        }

        setSuggestion("");
        setError(null);
        setStreaming(true);

        // 1) 본문 SSE 스트리밍
        streamReview({
            subject,
            bodyText: numberedOriginal,
            instruction,
            signal: ctrl.signal,
            onChunk: (txt) => setSuggestion(prev => prev + txt),
            onModel: (m)   => setModelName(m),
            onDone:  ()    => setStreaming(false),
            onError: (e)   => { setError(e.message || "요청 실패"); setStreaming(false); },
        });

        // 2) 제목 추천 — 본문 검토와 동시에 비동기 호출 (스트리밍 X, 단일 응답)
        //    본문 텍스트는 numberedOriginal 이 아닌 원문 텍스트를 보낸다 (AI 가 더 자연스럽게 추천).
        const plainBody = items.map(it => it.text).join("\n\n");
        if (plainBody.trim()) {
            setSubjectLoading(true);
            setSubjectSuggestions([]);
            mailApi.suggestSubject(plainBody)
                .then(res => {
                    const data = res?.data ?? res ?? {};
                    setSubjectSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
                })
                .catch(() => { /* 본문 검토엔 영향 없음, 조용히 fail */ })
                .finally(() => setSubjectLoading(false));
        }
    };

    /* 마운트 — 관리자 공통 prompt fetch 후 그 값으로 instruction 채우고, 첫 검토 자동 시작. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await mailApi.getAiPromptSetting();
                const data = res?.data ?? res ?? {};
                if (!cancelled && typeof data.prompt === "string" && data.prompt.trim()) {
                    setAdminPrompt(data.prompt);
                    setInstruction(data.prompt);
                }
            } catch { /* 실패해도 FALLBACK_INSTRUCTION 유지 */ }
            if (!cancelled) start();
        })();
        return () => { cancelled = true; abortRef.current?.abort(); };
        // eslint-disable-next-line
    }, []);

    /**
     * 단락별 비교 행:
     *  - 각 [N] 단락마다 { n, original, revised, hasRevised }
     *  - hasRevised 이면 InlineDiff 로 단어 단위 비교 렌더
     *  - 아직 AI 가 그 [N] 까지 출력 안 했으면 원본만 회색으로
     *
     *  스트리밍 도중에도 매 chunk 마다 parseNumberedWithFallback 으로 다시 파싱해
     *  완성된 단락부터 점진적으로 색깔이 입혀진다.
     */
    const paragraphRows = useMemo(() => {
        const aiMap = parseNumberedWithFallback(suggestion, items.length);
        return items.map((it, i) => ({
            n: i + 1,
            original: it.text,
            revised: aiMap.get(i + 1) || "",
            hasRevised: aiMap.has(i + 1) && (aiMap.get(i + 1) || "").trim().length > 0,
        }));
    }, [items, suggestion]);

    const handleApply = () => {
        if (!suggestion.trim()) {
            notify("아직 검토 결과가 없습니다", "warning");
            return;
        }
        if (!hasRevisable) {
            notify("적용할 변경 사항이 없습니다", "warning");
            return;
        }
        // [N] 매핑 → element textContent 교체 → 새 HTML
        // 모델이 [N] 형식을 지키지 않은 경우 fallback 으로 단락 분리해서 순서대로 매핑.
        const map = parseNumberedWithFallback(suggestion, items.length);
        if (map.size === 0) {
            notify("AI 응답을 파싱하지 못했습니다. 다시 받아주세요.", "warning");
            return;
        }
        const revisedHtml = applyRevisions(doc, items, map);
        onApply?.(revisedHtml);
    };

    const handleCopy = async () => {
        const ok = await copyToClipboard(suggestion);
        notify(ok ? "AI 제안을 복사했습니다" : "복사 실패", ok ? "success" : "error");
    };

    return (
        <Box sx={{
            width: "55%", height: "100%", display: "flex", flexDirection: "column",
            borderLeft: "1px solid #e2e8f0", bgcolor: "#f6f8fa",
        }}>
            {/* 헤더 */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{
                px: 2, py: 1.2, borderBottom: "1px solid #d0d7de", bgcolor: "#fff", flexShrink: 0,
            }}>
                <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: ds.colors.primary }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                    AI 검토
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                    {modelName || "—"}
                </Typography>
                {streaming && (
                    <Box sx={{
                        ml: 1, px: 1, py: 0.2, borderRadius: 1,
                        bgcolor: "#dbeafe", color: ds.colors.primary,
                        fontSize: 11, fontWeight: 700,
                        display: "flex", alignItems: "center", gap: 0.6,
                    }}>
                        <Box sx={{
                            width: 6, height: 6, borderRadius: "50%",
                            bgcolor: ds.colors.primary, animation: "pulse 1.2s infinite",
                            "@keyframes pulse": {
                                "0%, 100%": { opacity: 1 },
                                "50%":      { opacity: 0.3 },
                            },
                        }} />
                        검토 중
                    </Box>
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="복사">
                    <span>
                        <IconButton size="small" onClick={handleCopy} disabled={!suggestion.trim()}>
                            <ContentCopyRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="다시 검토">
                    <span>
                        <IconButton size="small" onClick={start} disabled={streaming}>
                            <RefreshRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="닫기">
                    <IconButton size="small" onClick={onClose}>
                        <CloseRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* 에러 */}
            {error && (
                <Box sx={{
                    px: 2, py: 1, fontSize: 12, color: "#991b1b",
                    bgcolor: "#fee2e2", borderBottom: "1px solid #fecaca",
                }}>
                    {error}
                </Box>
            )}

            {/* 안내 (색상 범례는 변경사항 표시 ON 일 때만) */}
            <Box sx={{
                px: 2, py: 0.8, fontSize: 11.5, color: "#57606a",
                bgcolor: "#fff", borderBottom: "1px solid #d0d7de",
                display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
            }}>
                <Box>공공기관·외부 발송 톤으로 다듬어 드립니다.</Box>
                {showDiff && (
                    <>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                            <Box component="span" sx={{
                                bgcolor: "#ffebe9", color: "#82071e", textDecoration: "line-through",
                                px: 0.5, borderRadius: 0.4, fontSize: 10.5,
                            }}>삭제</Box>
                        </Box>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                            <Box component="span" sx={{
                                bgcolor: "#dbeafe", color: "#1e3a8a", fontWeight: 700,
                                px: 0.5, borderRadius: 0.4, fontSize: 10.5,
                            }}>추가/수정</Box>
                        </Box>
                    </>
                )}
            </Box>

            {/* 제목 추천 — 본문 검토와 동시에 받음. 클릭 시 부모 ComposeModal 의 subject 로 적용 */}
            {(subjectLoading || subjectSuggestions.length > 0) && (
                <Box sx={{
                    px: 2, py: 0.8, bgcolor: "#fafbff",
                    borderBottom: "1px solid #d0d7de",
                    display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap",
                }}>
                    <Box sx={{ fontSize: 11.5, fontWeight: 700, color: "#5b21b6" }}>
                        ✨ 추천 제목
                    </Box>
                    {subjectLoading && (
                        <Box sx={{ fontSize: 11, color: "#94a3b8" }}>생성 중...</Box>
                    )}
                    {subjectSuggestions.map((s, i) => (
                        <Box key={i}
                            onClick={() => onApplySubject?.(s)}
                            sx={{
                                px: 0.8, py: 0.3, borderRadius: 0.8,
                                fontSize: 11.5, fontWeight: 500,
                                bgcolor: "#f5f3ff", color: "#5b21b6",
                                cursor: onApplySubject ? "pointer" : "default",
                                "&:hover": onApplySubject ? { bgcolor: "#ede9fe" } : undefined,
                            }}>
                            {s}
                        </Box>
                    ))}
                </Box>
            )}

            {/* AI 제안 단일 컬럼 — 헤더에 diff/preview 토글 + 변경 통계 */}
            <Box sx={{
                flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
                bgcolor: "#fff",
            }}>
                <Box sx={{
                    px: 1.5, py: 0.6, borderBottom: "1px solid #d0d7de", bgcolor: "#f6f8fa",
                    fontSize: 12, fontWeight: 700, color: "#57606a",
                    display: "flex", alignItems: "center", gap: 1,
                }}>
                    <Box>AI 제안</Box>
                    <Box sx={{ flex: 1 }} />
                    {/* 변경사항 보기 토글 — 기본 OFF (평문). 적용 전 마지막 검토 용도. */}
                    <Box
                        onClick={() => setShowDiff(v => !v)}
                        sx={{
                            display: "inline-flex", alignItems: "center", gap: 0.4,
                            px: 0.8, py: 0.2, borderRadius: 0.8, cursor: "pointer",
                            border: "1px solid",
                            borderColor: showDiff ? ds.colors.primary : "#d0d7de",
                            bgcolor: showDiff ? "#dbeafe" : "transparent",
                            color: showDiff ? ds.colors.primary : "#64748b",
                            fontSize: 11, fontWeight: 600,
                            "&:hover": { bgcolor: showDiff ? "#dbeafe" : "#f1f5f9" },
                        }}>
                        {showDiff ? "✓ 변경사항 표시" : "변경사항 표시"}
                    </Box>
                </Box>
                <Box sx={{
                    flex: 1, overflowY: "auto",
                    fontSize: 13, lineHeight: 1.7, color: "#1f2328",
                }}>
                    {paragraphRows.length === 0 ? (
                        <Box sx={{ p: 3, color: "#94a3b8", textAlign: "center" }}>
                            <LoadingDots label="AI가 답변을 작성중이에요"/>
                        </Box>
                    ) : paragraphRows.map((p) => (
                        <Box key={p.n} sx={{
                            px: 2, py: 0.7, borderBottom: "1px solid #f1f5f9",
                        }}>
                            {!p.hasRevised ? (
                                // 아직 AI 가 이 단락까지 안 흘러온 상태 — 원본을 회색으로 placeholder
                                <Box sx={{
                                    color: "#94a3b8",
                                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                                }}>
                                    {p.original}
                                </Box>
                            ) : showDiff ? (
                                // 변경사항 표시 ON — 단어 단위 inline diff (적용 전 마지막 검토)
                                <InlineDiff original={p.original} revised={p.revised} />
                            ) : (
                                // 변경사항 표시 OFF (default) — AI 결과만 평문으로
                                <Box sx={{
                                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                                }}>
                                    {p.revised}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* 프롬프트 입력 — AI 제안 아래. 기본값은 백엔드와 동일. */}
            <Box sx={{
                px: 2, py: 1, borderTop: "1px solid #d0d7de", bgcolor: "#fff", flexShrink: 0,
            }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#57606a", mb: 0.5 }}>
                    프롬프트 <Box component="span" sx={{ ml: 0.5, fontWeight: 400, color: "#94a3b8" }}>
                        (편집 후 "새 답변 받기" 를 누르면 반영됩니다)
                    </Box>
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} maxRows={6} size="small"
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    InputProps={{ sx: { fontSize: 11.5, fontFamily: "ui-monospace, 'SF Mono', Consolas, monospace", lineHeight: 1.55 } }}
                />
                {instruction !== adminPrompt && (
                    <Box sx={{ mt: 0.5, textAlign: "right" }}>
                        <ActionButton
                            variant="secondary"
                            startIcon={false}
                            onClick={() => setInstruction(adminPrompt)}
                            label="기본값으로 되돌리기"
                            sx={{ fontSize: 11 }}
                        />
                    </Box>
                )}
            </Box>

            {/* 하단 액션 */}
            <Box sx={{
                px: 2, py: 1.2, borderTop: "1px solid #d0d7de",
                display: "flex", gap: 1, justifyContent: "flex-end",
                bgcolor: "#fff", flexShrink: 0,
            }}>
                <ActionButton variant="cancel" onClick={onClose} label="닫기" />
                <ActionButton
                    variant="refresh"
                    onClick={start}
                    disabled={streaming}
                    label="새 답변 받기"
                    sx={{
                        borderColor: "#7c3aed", color: "#7c3aed",
                        "&:hover": { borderColor: "#6d28d9", bgcolor: "#f5f3ff" },
                        "&.Mui-disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
                    }}
                />
                <ActionButton
                    variant="confirm"
                    onClick={handleApply}
                    disabled={streaming || !suggestion.trim()}
                    label="적용"
                />
            </Box>
        </Box>
    );
}
